import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Tooltip,
  Paper,
  Divider,
  useTheme,
  alpha,
  Avatar,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { getBracketNames, localizeBracketLabel } from "../utils/bracketNames";
import { useOrganization } from "../contexts/OrganizationContext";
import SafeAvatar from "./SafeAvatar";
import TournamentMatchBreakdown from "./TournamentMatchBreakdown";

interface TournamentHistoryEntry {
  tournamentId: string;
  tournamentName: string;
  tournamentLevel: string;
  startDate: Date | string;
  endDate?: Date | string;
  position: number | null;
  positionLabel: string;
  cupType: "Masters Cup" | "Tour Cup" | null;
  eliminatedRound?: string | null;
  partnerId: string;
  partnerName: string;
  partnerProfilePicture?: string;
  teamId: string;
  points: number;
}

interface TournamentHistoryChartProps {
  playerId: string;
}

const TournamentHistoryChart: React.FC<TournamentHistoryChartProps> = ({
  playerId,
}) => {
  const theme = useTheme();
  const { currentOrg } = useOrganization();
  const bracketNames = getBracketNames(currentOrg?.slug);
  const [history, setHistory] = useState<TournamentHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());

  const handleAccordionChange = (tournamentId: string) => (
    _: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (isExpanded) next.add(tournamentId);
      else next.delete(tournamentId);
      return next;
    });
    if (isExpanded) {
      setLoadedIds((prev) => (prev.has(tournamentId) ? prev : new Set(prev).add(tournamentId)));
    }
  };

  useEffect(() => {
    fetchTournamentHistory();
  }, [playerId]);

  const fetchTournamentHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest(
        `/api/players/${playerId}/tournament-history`
      );
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        setError("Failed to load tournament history");
      }
    } catch (err) {
      console.error("Error fetching tournament history:", err);
      setError("An error occurred while loading tournament history");
    } finally {
      setLoading(false);
    }
  };

  const getPositionColor = (position: number | null, cupType: string | null) => {
    if (!position) return theme.palette.grey[500];
    if (position === 1) return theme.palette.primary.main; // Primary color for 1st
    if (position === 2) return theme.palette.primary.light; // Lighter for 2nd
    if (position === 3) return theme.palette.secondary.main; // Secondary for 3rd
    return theme.palette.grey[600]; // Neutral for others
  };

  const getPositionBadge = (position: number | null) => {
    if (!position) return null;
    return position;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardContent>
          <Box
            sx={{
              textAlign: "center",
              py: 4,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <TrophyIcon sx={{ fontSize: 48, color: "grey.400" }} />
            </Box>
            <Typography variant="h6" color="text.secondary">
              No tournament history yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Tournament results will appear here after participating in
              completed tournaments
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Visual Timeline */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
            }}
          >
            <TrendingUpIcon fontSize="small" />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Performance Timeline
          </Typography>
        </Box>

        <Box sx={{ position: "relative", pl: { xs: 2, sm: 4 } }}>
          {/* Timeline line */}
          <Box
            sx={{
              position: "absolute",
              left: { xs: 8, sm: 16 },
              top: 0,
              bottom: 0,
              width: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.2),
            }}
          />

          {history.map((entry, index) => {
            const positionColor = getPositionColor(
              entry.position,
              entry.cupType
            );
            const positionBadge = getPositionBadge(entry.position);

            return (
              <Box
                key={entry.tournamentId}
                sx={{
                  position: "relative",
                  mb: 3,
                  pl: { xs: 3, sm: 4 },
                }}
              >
                {/* Timeline dot */}
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: 0, sm: 8 },
                    top: 8,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    bgcolor: positionColor,
                    border: `3px solid ${alpha(positionColor, 0.2)}`,
                    boxShadow: `0 0 0 4px ${alpha(positionColor, 0.1)}`,
                  }}
                />

                <Accordion
                  expanded={expandedIds.has(entry.tournamentId)}
                  onChange={handleAccordionChange(entry.tournamentId)}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderLeft: `4px solid ${positionColor}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    "&:before": { display: "none" },
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: 3,
                    },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Grid container spacing={2} alignItems="center" sx={{ width: "100%" }}>
                      <Grid item xs={12} sm={12} md={4}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          {positionBadge && (
                            <Box
                              sx={{
                                height: 44,
                                minWidth: "fit-content",
                                px: 2,
                                borderRadius: 1,
                                bgcolor: alpha(positionColor, 0.15),
                                border: `2px solid ${positionColor}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: "bold",
                                  color: positionColor,
                                  fontSize: "1.1rem",
                                  fontFamily: "monospace",
                                  lineHeight: 1,
                                }}
                              >
                                {positionBadge}
                              </Box>
                            </Box>
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold", lineHeight: 1.2 }}
                            >
                              {entry.tournamentName}
                            </Typography>
                            <Chip
                              label={entry.tournamentLevel}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CalendarIcon
                            sx={{ fontSize: 16, color: "text.secondary" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {new Date(entry.startDate).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6} md={2}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <SafeAvatar
                            src={entry.partnerProfilePicture}
                            alt={entry.partnerName}
                            fallbackText={entry.partnerName}
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: "0.875rem",
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {entry.partnerName}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={12} md={3}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: { xs: "flex-start", md: "flex-end" },
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            label={localizeBracketLabel(entry.positionLabel, currentOrg?.slug)}
                            sx={{
                              bgcolor: alpha(positionColor, 0.15),
                              color: positionColor,
                              fontWeight: "bold",
                              borderColor: positionColor,
                              border: 1,
                            }}
                          />
                          {entry.cupType && (
                            <Chip
                              label={entry.cupType === "Masters Cup" ? bracketNames.primary : bracketNames.secondary}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                fontWeight: "bold",
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails>
                    {loadedIds.has(entry.tournamentId) && (
                      <TournamentMatchBreakdown
                        playerId={playerId}
                        tournamentId={entry.tournamentId}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default TournamentHistoryChart;
